import React, { useState } from 'react';
import PageHero from '@/components/PageHero';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { Phone, MapPin, Facebook, Mail, Send } from 'lucide-react';
import { SITE } from '@/data/content';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: 'Please fill all fields', description: 'Name, email and message are required.' });
      return;
    }
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast({
        title: 'Message sent',
        description: 'Thank you — we’ll be in touch shortly.',
      });
      setForm({ name: '', email: '', message: '' });
    }, 900);
  };

  return (
    <div>
      <SEO
        title="Contact"
        description="Contact Kress & Associates to schedule the Drug and Alcohol Awareness program at your school or district. Located in Erie, Pennsylvania."
        path="/contact"
      />
      <PageHero
        title="Send us a message"
        subtitle="Interested in bringing the program to your school or district? Get in touch."
        image="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1920&q=80"
        crumbs={['Contact']}
      />
      <div className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-[1fr_360px] gap-10">
        <form onSubmit={submit} className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
          <h2 className="font-serif text-2xl font-bold text-[#0b2545] mb-6">Contact Form</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-slate-700">Name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@school.edu"
                className="mt-2"
              />
            </div>
          </div>
          <div className="mt-5">
            <Label htmlFor="message" className="text-slate-700">Message</Label>
            <Textarea
              id="message"
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your school and the audience you’d like us to reach."
              className="mt-2"
            />
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="mt-6 bg-[#0b2545] hover:bg-[#123a6b] text-white"
          >
            {sending ? 'Sending...' : (<><Send className="mr-2 h-4 w-4" /> Send Message</>)}
          </Button>
        </form>

        <aside className="space-y-4">
          <h3 className="font-serif text-xl font-bold text-[#0b2545] mb-2">Other ways to reach us</h3>
          <InfoRow icon={<Facebook className="h-5 w-5" />} label="Facebook" href={SITE.facebook}>
            Kress-Associates
          </InfoRow>
          <InfoRow icon={<Phone className="h-5 w-5" />} label="Phone" href={`tel:${SITE.phone.replace(/[^0-9]/g, '')}`}>
            {SITE.phone}
          </InfoRow>
          <InfoRow icon={<Mail className="h-5 w-5" />} label="Email" href="mailto:info@drugawarenessusa.net">
            info@drugawarenessusa.net
          </InfoRow>
          <div className="bg-white p-5 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="h-5 w-5 text-amber-500" />
              <span className="font-semibold text-[#0b2545]">Mailing Address</span>
            </div>
            <div className="text-sm text-slate-600 leading-relaxed pl-8">
              {SITE.address.line1}<br />
              {SITE.address.line2}<br />
              {SITE.address.line3}<br />
              {SITE.address.country}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, href, children }) {
  return (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel="noopener noreferrer"
      className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all group"
    >
      <div className="h-10 w-10 rounded-md bg-[#0b2545] text-amber-400 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-semibold text-[#0b2545]">{children}</div>
      </div>
    </a>
  );
}
